import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import api from '../../../lib/api';

const statusConfig = {
  active: { color: 'var(--color-success)', bg: '#D1FAE5', label: 'Active' },
  inactive: { color: 'var(--color-error)', bg: '#FEE2E2', label: 'Inactive' },
  suspended: { color: 'var(--color-warning)', bg: '#FEF3C7', label: 'Suspended' },
  trial: { color: 'var(--color-primary)', bg: '#EFF6FF', label: 'Trial' },
};

const MemberDataGrid = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [sortField, setSortField] = useState('join_date');
  const [sortDir, setSortDir] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data } = await api.get('/members', { params: { limit: 50 } });

      const enriched = (data || [])?.map(m => ({
        id: m?.id,
        name: `${m?.first_name || ''} ${m?.last_name || ''}`?.trim() || '—',
        plan: m?.package_name || 'No Package',
        status: m?.member_status,
        joinDate: m?.join_date ? new Date(m?.join_date)?.toLocaleDateString() : '—',
        visits: 0,
        engagementScore: 0,
        ltv: '$0',
        paymentStatus: 'paid',
      }));

      setMembers(enriched);
    } catch (err) {
      console.error('Error fetching members grid:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  const toggleSelect = (id) => {
    setSelected(prev => prev?.includes(id) ? prev?.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    setSelected(selected?.length === filtered?.length ? [] : filtered?.map(m => m?.id));
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const filtered = members?.filter(m => filterStatus === 'all' || m?.status === filterStatus);
  const sorted = [...filtered]?.sort((a, b) => {
    const aVal = a?.[sortField]; const bVal = b?.[sortField];
    if (typeof aVal === 'number') return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    return sortDir === 'asc' ? String(aVal)?.localeCompare(String(bVal)) : String(bVal)?.localeCompare(String(aVal));
  });

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown' : 'ChevronsUpDown'}
      size={13}
      color={sortField === field ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
    />
  );

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 lg:p-5 border-b border-[var(--color-border)] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-base lg:text-lg font-semibold font-heading text-[var(--color-foreground)]">
            Member Profiles
          </h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-caption mt-0.5">
            {loading ? 'Loading...' : `${sorted?.length} members · ${selected?.length} selected`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-[var(--color-muted)] rounded-lg p-0.5">
            {['all', 'active', 'inactive', 'trial']?.map(s => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-2.5 py-1 rounded-md text-xs font-caption font-medium capitalize transition-all ${
                  filterStatus === s
                    ? 'bg-[var(--color-card)] text-[var(--color-primary)] shadow-sm'
                    : 'text-[var(--color-muted-foreground)]'
                }`}
              >
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
          {selected?.length > 0 && (
            <Button variant="outline" size="sm" iconName="Send" iconPosition="left" iconSize={13}>
              Bulk Action ({selected?.length})
            </Button>
          )}
          <Button variant="default" size="sm" iconName="Download" iconPosition="left" iconSize={13}>
            Export
          </Button>
        </div>
      </div>
      {error && (
        <div className="mx-4 mt-3 p-2 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
          <p className="text-xs text-[var(--color-error)] font-caption">{error}</p>
        </div>
      )}
      {loading ? (
        <div className="p-8 flex items-center justify-center">
          <div className="flex items-center gap-2 text-[var(--color-muted-foreground)]">
            <Icon name="Loader2" size={16} color="currentColor" className="animate-spin" />
            <span className="text-sm font-caption">Loading members...</span>
          </div>
        </div>
      ) : sorted?.length === 0 ? (
        <div className="p-8 text-center text-sm text-[var(--color-muted-foreground)] font-caption">
          No members found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-[var(--color-muted)]">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selected?.length === sorted?.length && sorted?.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded accent-[var(--color-primary)] cursor-pointer"
                  />
                </th>
                {[['name','Member'],['plan','Plan'],['status','Status'],['joinDate','Join Date'],['visits','Visits'],['engagementScore','Engagement'],['ltv','LTV']]?.map(([field, label]) => (
                  <th key={field} className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort(field)}
                      className="flex items-center gap-1 text-xs font-semibold font-caption text-[var(--color-muted-foreground)] uppercase tracking-wide hover:text-[var(--color-foreground)] transition-colors"
                    >
                      {label} <SortIcon field={field} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {sorted?.map(m => {
                const cfg = statusConfig?.[m?.status] || statusConfig?.active;
                return (
                  <tr key={m?.id} className={`hover:bg-[var(--color-muted)]/30 transition-colors ${selected?.includes(m?.id) ? 'bg-[var(--color-primary)]/5' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected?.includes(m?.id)}
                        onChange={() => toggleSelect(m?.id)}
                        className="w-4 h-4 rounded accent-[var(--color-primary)] cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-[var(--color-primary)]/15 flex items-center justify-center text-xs font-bold text-[var(--color-primary)] flex-shrink-0">
                          {m?.name?.charAt(0)}
                        </div>
                        <span className="text-sm font-medium font-body text-[var(--color-foreground)] whitespace-nowrap">{m?.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-caption capitalize">{m?.plan}</td>
                    <td className="px-4 py-3">
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-caption font-medium"
                        style={{ backgroundColor: cfg?.bg, color: cfg?.color }}
                      >
                        {cfg?.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-caption whitespace-nowrap">{m?.joinDate}</td>
                    <td className="px-4 py-3 font-data text-[var(--color-foreground)]">{m?.visits}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-[var(--color-muted)] rounded-full h-1.5 min-w-[60px]">
                          <div
                            className="h-1.5 rounded-full"
                            style={{ width: `${m?.engagementScore}%`, backgroundColor: m?.engagementScore >= 70 ? 'var(--color-success)' : m?.engagementScore >= 40 ? 'var(--color-warning)' : 'var(--color-error)' }}
                          />
                        </div>
                        <span className="text-xs font-data text-[var(--color-muted-foreground)]">{m?.engagementScore}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-data font-medium text-[var(--color-foreground)]">{m?.ltv}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MemberDataGrid;